import React, {useEffect, useState} from "react";
import ReactDOMServer from 'react-dom/server'
import ProductAction from "../ProductAction";
import SellerInfo from "../SellerInfo";
import {useLocation} from 'react-router-dom';
import {Column, Container, Description, Gallery, Panel, Row,} from "./styles";
import {push} from "connected-react-router";
import {connect} from "react-redux";
import {StoreType} from "../../../../../infra/store/reducers";
import {Box, Rating} from "@mui/material";
import Typography from "@mui/material/Typography";
import {styled} from "@mui/material/styles";
import {getWindowDimensions} from "../../../../../common/Util";
import {seedProduct} from "../../redux/action";
import {IProduct} from "../../redux/productReducer";
import {addCartItem} from "../../../Marketplace/redux/action";
import {seedCredentials, setForm} from "../../../../external/Login/redux/action";
import type {FilterReducer} from "../../../Filters/redux/filtersReducer";
import {seedFilters} from "../../../Filters/redux/action";
import {fetchVinhos} from "../../../ListaVinhos/redux/action";
import {Helmet} from 'react-helmet-async';
import MetaTags from 'react-meta-tags';
import CircularIndeterminate from "../../../../../common/components/LoadingScreen/LoadingComponent";
import {getStructuredDataForProductPage} from "../../../../../common/StructuredDataUtil";
import {EMAIL_HELP_RESTAURANTE, PASSWORD_HELP_RESTAURANTE} from "../../../../../common/constantes";

const StyledRating = styled(Rating)({
    '& .MuiRating-iconFilled': {
        color: '#7b58f1',
    },
    '& .MuiRating-iconHover': {
        color: '#7049f6',
    },
});

interface IProductProps {
    navigateHome: Function;
    navigateCart: Function;
    seedProductInfo: Function;
    setForm: Function;
    seedCredentials: Function;
    seedFilters: Function;
    addCart: Function;
    fetchListaVinhos: Function;
    goToCart: Function;
    product: IProduct;
    product404: boolean;
    filtros: FilterReducer;
    match: {
        params: {
            id: string
        }
    }
}

const Product: (props: IProductProps) => JSX.Element = (props: IProductProps) => {

    const {product, setForm, seedCredentials, seedFilters} = props;

    const location = useLocation();

    const { state = {} } = location;
    let { statePublic = false, hasStateSubscription = false, locationCnpj = true, isPublicLink = true } = state;

    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    const [loadedSrc, setLoadedSrc] = React.useState(null);
    const onImageLoad = (e: any) => setLoadedSrc(e.target.currentSrc);

    useEffect(() => {
        seedFilters();
    }, [seedCredentials, seedFilters, setForm])

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'auto'
        });

        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const {seedProductInfo, match} = props;
    const {params} = match;
    const {id} = params;

    useEffect(() => {
        let queryParams = null;
        if ((isPublicLink) && (statePublic || hasStateSubscription || locationCnpj)) {
            queryParams = {
                isPublicLink,
                locationCnpj: (locationCnpj !== null) ? locationCnpj : true,
                state: statePublic,
                hasStateSubscription: (hasStateSubscription !== null) ? hasStateSubscription : true
            }
        }

        const email = localStorage.getItem(EMAIL_HELP_RESTAURANTE);
        const senha = localStorage.getItem(PASSWORD_HELP_RESTAURANTE);

        if(email && senha) {
            queryParams = null;
        }

        seedProductInfo(id, queryParams);
    }, [seedProductInfo, id]);

    const imageVariants = [
        {
            url: product.wineImageURL,
            width: 500
        },
        {
            url: product.wineImageURL,
            width: 1000
        },
        {
            url: product.wineImageURL,
            width: 3200
        }
    ];

    const srcSet = imageVariants
        .map(variant => `${variant.url} ${variant.width}w`)
        .join(",");

    const productNamePrefix =
        product?.wineName?.toLowerCase().startsWith('vinho') ||
        product?.wineName?.toLowerCase().startsWith('vho') ? '' : 'Vinho ';

    const productName = productNamePrefix + product.wineName;
    const productNameWithoutPrefix = productName.replace(/vinho /ig, '').replace(/vho /ig, '');

    const webPageTitle = `${productName} | Helpvino Marketplace`;
    const webPageCanonicalURL = `https://dashboard.helpvino.com/produto/${props.match.params.id}/${product.seoName}`;
    const webPageDescriptionText = `Compre o Vinho ${product.wineTypeName} ${productNameWithoutPrefix} na Helpvino. Visite nosso Marketplace e confira todos os vinhos disponíveis`;
    const webPageDescription = ReactDOMServer.renderToStaticMarkup(<>{webPageDescriptionText} &#127863;</>);

    return (
        <Container>
            <Helmet prioritizeSeoTags>
                <title>{webPageTitle}</title>
                <script type="application/ld+json">
                    {JSON.stringify(getStructuredDataForProductPage(product))}
                </script>
            </Helmet>
            <MetaTags>
                <meta httpEquiv="x-ua-compatible" content="ie=edge" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta name="description" content={webPageDescription} />
                <meta name="keywords" content={`${product.wineName} ${product.grapeTypeName}, preço do vinho ${product.wineName}, marketplace produto ${product.wineName}, home ${product.wineName}, preço do vinho ${product.wineName} ${product.grapeTypeName}, ${(props.filtros.listaPais.length && product.countryId) && props.filtros.listaPais.find(pais => pais.countryId === product.countryId).countryName}`}/>
                <meta name="robots" content="index, follow, noodp" />
                <meta name="googlebot" content="index, follow" />
                <meta name="google" content="notranslate" />
                <meta name="format-detection" content="telephone=no" />

                <meta property="og:title" content={webPageTitle} />
                <meta property="og:type" content="product" />
                <meta property="og:description" content={webPageDescription} />
                <meta property="og:url" content={webPageCanonicalURL} />
                <meta property="og:site_name" content={product.seoName} />
                <meta property="og:image" content={product.wineImageURL} />

                <meta property="twitter:title" content={webPageTitle} />
                <meta property="twitter:description" content={webPageDescription} />
                <meta property="twitter:url" content={webPageCanonicalURL} />

                <link rel="canonical" href={webPageCanonicalURL} />
            </MetaTags>
            <Row>
                <a href="/" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.navigateHome();
                }}>Home</a>
                <a href="/#" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.navigateCart();
                }}>Marketplace</a>
                <a href="/#" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }} className={'last-child-to-row'}>Produto</a>
            </Row>

            <Panel>
                <Column className={'product-column'}>
                    <Gallery>
                        <picture>
                            <source type="image/webp" srcSet={product.wineImageURL}/>
                            <source type="image/png" srcSet={product.wineImageURL}/>
                            <source type="image/jpg" srcSet={product.wineImageURL}/>
                            <source type="image/jpeg" srcSet={product.wineImageURL}/>
                            <CircularIndeterminate componentShow={(
                                <img
                                    alt={product.wineName}
                                    src={product.wineImageURL}
                                    onLoad={onImageLoad}
                                    srcSet={srcSet}
                                />
                            )} />
                        </picture>

                    </Gallery>

                    {windowDimensions.width > 999 && (
                        <Info product={product}/>
                    )}

                    <CircularIndeterminate notShowLoading componentShow={(
                        <Box
                            sx={{
                                display: 'flex',
                                transform: 'scale(0.7)',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                paddingTop: '7px',
                                position: 'absolute',
                                top: 5,
                                left: 5
                            }}
                        >
                            <Box sx={{
                                ml: 2,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                '& > img': {
                                    mr: 2,
                                    flexShrink: 0,
                                    justifyContent: 'center'
                                }
                            }}>
                                <Typography gutterBottom variant="body2" color="text.secondary" sx={{
                                    fontWeight: '700',
                                    'color': '#6E4FF3',
                                    fontSize: '20px'
                                }}>{product.heviScore}</Typography>
                            </Box>
                            {product.heviScore && (
                                <StyledRating
                                    readOnly
                                    name="customized-color"
                                    defaultValue={product.heviScore}
                                    getLabelText={(value) => `${value} Heart${value !== 1 ? 's' : ''}`}
                                    precision={0.5}
                                />
                            )}
                        </Box>
                    )} />
                </Column>

                <Column>
                    <ProductAction product={product} addToCart={props.addCart} goToCart={props.goToCart}
                                   goToLogin={props.navigateHome}/>
                    <SellerInfo filtros={props.filtros} product={product}/>

                    {/*<WarrantySection />*/}
                    {/*<WarrantySection />*/}
                    {/*<WarrantySection />*/}

                    {windowDimensions.width < 1000 && (
                        <Info product={product}/>
                    )}
                </Column>
            </Panel>

            <Column className={'opacity-invisible'}>
                <h1>{`Vinho ${product.wineName} ${product.grapeTypeName} ${(props.filtros.listaPais.length && product.countryId) && props.filtros.listaPais.find(pais => pais.countryId === product.countryId).countryName} Home Produto Marketplace `}</h1>
                <h2>{`Vinho ${product.wineName} ${product.grapeTypeName}  ${(props.filtros.listaPais.length && product.countryId) && props.filtros.listaPais.find(pais => pais.countryId === product.countryId).countryName} Home Produto Marketplace `}</h2>
                <h3>{`Vinho ${product.wineName} ${product.grapeTypeName}  ${(props.filtros.listaPais.length && product.countryId) && props.filtros.listaPais.find(pais => pais.countryId === product.countryId).countryName} Home Produto Marketplace `}</h3>
                <h5>{`Vinho ${product.wineName} ${product.grapeTypeName}  ${(props.filtros.listaPais.length && product.countryId) && props.filtros.listaPais.find(pais => pais.countryId === product.countryId).countryName} Home Produto Marketplace `}</h5>
                <h6>{`Vinho ${product.wineName} ${product.grapeTypeName}  ${(props.filtros.listaPais.length && product.countryId) && props.filtros.listaPais.find(pais => pais.countryId === product.countryId).countryName} Home Produto Marketplace `}</h6>
            </Column>
        </Container>
    );
};

const Info = (props: { product: IProduct }) => (
    <Description>
        <h2>Descrição</h2>

        <p>
            {props.product.wineNotes}
            <br/>
        </p>
    </Description>
);

const mapStateToProps = (store: StoreType) => {
    return {
        product404: store.PRODUCT.product_404,
        product: store.PRODUCT.productInfo,
        filtros: store.FILTERS
    };
};

const mapToDispatchToProps = (dispatch: any) => {
    return {
        navigateHome: () => dispatch(push(`/`)),
        navigateCart: () => dispatch(push(`/marketplace`)),
        goToCart: () => dispatch(push(`/marketplace?goToCart=true`)),
        setForm: (attr: string, value: string) => dispatch(setForm(attr, value)),
        seedCredentials: () => dispatch(seedCredentials()),
        seedFilters: () => dispatch(seedFilters()),
        fetchListaVinhos: () => dispatch(fetchVinhos([])),
        seedProductInfo: (productId: string, queryParams: {state: string, hasStateSubscription: boolean, locationCnpj: boolean|null  }) => dispatch(seedProduct(productId, queryParams)),
        addCart: (orderId: {
            legalRepresentativeId: number,
            packages: number
        }, packageQuantity: number) => dispatch(addCartItem(orderId, packageQuantity)),
    };
};

// @ts-ignore
export default connect(mapStateToProps, mapToDispatchToProps)(Product);
